import React from 'react';
import { usePropertyPanel } from '../PropertyPanelContext';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { PaddingControl, BorderRadiusControl, StyleControl, SkinSelector } from '../CommonProperties';

const ProductListProperties = () => {
  // 선택된 컴포넌트 가져오기
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // 컨텍스트에서 필요한 값과 함수들 가져오기
  const {
    devicePropertyTab,
    handleComponentPropsChange
  } = usePropertyPanel();
  
  return (
    <>
      {/* 스킨 선택 */}
      <SkinSelector
        selectedComponent={selectedComponent}
        onSkinChange={(skinId) => handleComponentPropsChange('skin', skinId)}
      />

      {/* 상품 목록 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3h18v18H3z" stroke="currentColor" strokeWidth="2" fill="none"/>
            <path d="M3 9h18M9 3v18" stroke="currentColor" strokeWidth="2"/>
          </svg>
          상품 목록 설정
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>한 줄당 상품 수</label>
          <input 
            type="number" 
            min="1"
            max="6"
            value={selectedComponent.componentProps?.itemsPerRow || 4}
            onChange={(e) => handleComponentPropsChange('itemsPerRow', parseInt(e.target.value) || 4)}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>페이지당 상품 수</label>
          <input 
            type="number" 
            min="1"
            max="100"
            value={selectedComponent.componentProps?.itemsPerPage || 20}
            onChange={(e) => handleComponentPropsChange('itemsPerPage', parseInt(e.target.value) || 20)}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>가격 표시</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-show-price"
              type="checkbox" 
              checked={selectedComponent.componentProps?.showPrice !== false}
              onChange={(e) => handleComponentPropsChange('showPrice', e.target.checked)}
            />
            <label htmlFor="toggle-show-price" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>재고 정보 표시</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-stock-list"
              type="checkbox" 
              checked={selectedComponent.componentProps?.showStock === true}
              onChange={(e) => handleComponentPropsChange('showStock', e.target.checked)}
            />
            <label htmlFor="toggle-stock-list" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>장바구니 버튼</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-show-cart"
              type="checkbox" 
              checked={selectedComponent.componentProps?.showAddToCart !== false}
              onChange={(e) => handleComponentPropsChange('showAddToCart', e.target.checked)}
            />
            <label htmlFor="toggle-show-cart" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>페이지네이션</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-pagination"
              type="checkbox" 
              checked={selectedComponent.componentProps?.showPagination !== false}
              onChange={(e) => handleComponentPropsChange('showPagination', e.target.checked)}
            />
            <label htmlFor="toggle-pagination" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
      </div>

      {/* 적립금 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
          </svg>
          적립금 설정
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>적립금 표시</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-show-reward-points"
              type="checkbox" 
              checked={selectedComponent.componentProps?.showRewardPoints === true}
              onChange={(e) => handleComponentPropsChange('showRewardPoints', e.target.checked)}
            />
            <label htmlFor="toggle-show-reward-points" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>
        
        {selectedComponent.componentProps?.showRewardPoints && (
          <>
            <div className="property-row">
              <label style={{ minWidth: '30%' }}>적립 기준</label>
              <select 
                value={selectedComponent.componentProps?.rewardPointBase || 'pv'}
                onChange={(e) => handleComponentPropsChange('rewardPointBase', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="pv">PV 기준</option>
                <option value="price">판매가 기준</option>
              </select>
            </div>
            
            <div className="property-row">
              <label style={{ minWidth: '30%' }}>적립 비율 (%)</label>
              <input 
                type="number" 
                min="0"
                max="100"
                step="0.1"
                value={selectedComponent.componentProps?.rewardPointPercentage || 1}
                onChange={(e) => handleComponentPropsChange('rewardPointPercentage', parseFloat(e.target.value) || 1)}
              />
            </div>
            
            <div className="property-row">
              <label style={{ minWidth: '30%' }}>적립금 라벨</label>
              <input 
                type="text" 
                value={selectedComponent.componentProps?.rewardPointLabel || '적립금'}
                onChange={(e) => handleComponentPropsChange('rewardPointLabel', e.target.value)}
              />
            </div>
            
            <div className="property-row">
              <label style={{ minWidth: '30%' }}>표시 형식</label>
              <select 
                value={selectedComponent.componentProps?.rewardPointFormat || 'amount'}
                onChange={(e) => handleComponentPropsChange('rewardPointFormat', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="amount">금액만 표시</option>
                <option value="percentage">퍼센트만 표시</option>
                <option value="both">금액과 퍼센트 모두 표시</option>
              </select>
            </div>
          </>
        )}
      </div>

      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
            <path d="M9 9V21" stroke="currentColor" strokeWidth="2" />
            <path d="M15 9V21" stroke="currentColor" strokeWidth="2" />
          </svg>
          필터링 설정
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>포함할 상품 ID</label>
          <input 
            type="text" 
            value={selectedComponent.componentProps?.include_product_ids || ''}
            onChange={(e) => handleComponentPropsChange('include_product_ids', e.target.value)}
          />
          <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            쉼표로 구분하여 입력 (예: 73,74,75)
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>제외할 상품 ID</label>
          <input 
            type="text" 
            value={selectedComponent.componentProps?.exclude_product_ids || ''}
            onChange={(e) => handleComponentPropsChange('exclude_product_ids', e.target.value)}
          />
          <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            쉼표로 구분하여 입력 (예: 74,76)
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>포함할 카테고리 ID</label>
          <input 
            type="text" 
            value={selectedComponent.componentProps?.include_category_ids || ''}
            onChange={(e) => handleComponentPropsChange('include_category_ids', e.target.value)}
          />
          <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            쉼표로 구분하여 입력 (예: 14,8)
          </div>
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>제외할 카테고리 ID</label>
          <input 
            type="text" 
            value={selectedComponent.componentProps?.exclude_category_ids || ''}
            onChange={(e) => handleComponentPropsChange('exclude_category_ids', e.target.value)}
          />
          <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            쉼표로 구분하여 입력 (예: 16,17)
          </div>
        </div>
      </div>

      {/* 스타일 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          스타일 설정
        </div>
        
        <div className="property-row">
          <label>가격 색상</label>
          <input 
            type="color" 
            value={selectedComponent.componentProps?.priceColor || '#ff6b6b'}
            onChange={(e) => handleComponentPropsChange('priceColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
        
        <div className="property-row">
          <label>장바구니 버튼 색상</label>
          <input 
            type="color" 
            value={selectedComponent.componentProps?.cartButtonColor || '#007bff'}
            onChange={(e) => handleComponentPropsChange('cartButtonColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
        
        <div className="property-row">
          <label>재고 텍스트 색상</label>
          <input 
            type="color" 
            value={selectedComponent.componentProps?.stockTextColor || '#28a745'}
            onChange={(e) => handleComponentPropsChange('stockTextColor', e.target.value)}
            style={{ width: '100%', height: '32px' }}
          />
        </div>
      </div>
      
      {/* 공통 속성 컴포넌트 사용 */}
      {/* <PaddingControl />
      <BorderRadiusControl />
      <StyleControl /> */}
    </>
  );
};

export default ProductListProperties; 